import React from 'react';

import ActionFunctionButton from 'notebook/structured-tables/action-functions/ActionFunctionButton';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import {
  StructuredTableMappingsGlobalState,
  StructuredTableMappingsStateManager,
} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import {
  OverflowMenuActionFunction,
  TableActionFunctionParameters,
  ToolbarButtonActionFunction,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableIndicatorStatus} from 'notebook/tables/TableConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import {phraseToCamelCase} from 'util/NameUtil';

export type ActionParamCallback<TTableMetadata, TRowMetadata, T> = (
  params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
) => T;

export interface MakeSimpleActionFunctionParams<TTableMetadata, TRowMetadata> {
  // A short name for the action, suitable for display in a button or menu item.
  label: string | ActionParamCallback<TTableMetadata, TRowMetadata, string>;

  // Displayed on the button or menu item.
  icon:
    | BenchlingIconName
    | JSX.Element
    | ActionParamCallback<TTableMetadata, TRowMetadata, BenchlingIconName | JSX.Element>;
  iconAltText?: string;

  // Called when the user clicks the button or menu item.
  onInvoke: ActionParamCallback<TTableMetadata, TRowMetadata, void>;

  // The design parameters are ignored when an action function is rendered as an overflow menu item
  buttonDesign?: {
    // Defaults to ButtonStyle.TERTIARY_GRAY
    buttonStyle?: ButtonStyle | ActionParamCallback<TTableMetadata, TRowMetadata, ButtonStyle>;

    // Defaults to false
    showTextOnButton?: boolean | ActionParamCallback<TTableMetadata, TRowMetadata, boolean>;
  };

  // Return the tooltip text, or null to hide the tooltip. If undefined, the tooltip defaults to the action
  // function label (for buttons) or no tooltip (for overflow menu items). This should never return just the
  // action name because such a tooltip would be redundant for menu items.
  getTooltip?: ActionParamCallback<TTableMetadata, TRowMetadata, string | null | JSX.Element>;

  // Return whether the button should be disabled. If not provided, the button will always be enabled. If the
  // action is not permitted in locked entries, this should return true if params.locked is true.
  getDisabled?: ActionParamCallback<TTableMetadata, TRowMetadata, boolean>;

  // Return whether the button should be hidden completely.
  getHidden?: ActionParamCallback<TTableMetadata, TRowMetadata, boolean>;
}

/**
 * Create an ActionFunction that can be placed in either the toolbar or the overflow menu.
 */
export default function makeSimpleActionFunction<TTableMetadata, TRowMetadata>(
  params: MakeSimpleActionFunctionParams<TTableMetadata, TRowMetadata>
): ToolbarButtonActionFunction<TTableMetadata, TRowMetadata> &
  OverflowMenuActionFunction<TTableMetadata, TRowMetadata> {
  const renderToolbarButton = (
    actionFunctionParams: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ): JSX.Element => {
    const label = evaluateFunctionalParam(params.label, actionFunctionParams);
    // An undefined callback means use the default tooltip, while a defined callback that returns null means disable the tooltip.
    const tooltip = params.getTooltip?.(actionFunctionParams);
    const tooltipText = typeof tooltip === 'string' ? tooltip : undefined;
    const shouldShowTextOnButton = evaluateFunctionalParam(
      params.buttonDesign?.showTextOnButton ?? false,
      actionFunctionParams
    );
    const buttonText = shouldShowTextOnButton ? label : undefined;
    return (
      <ActionFunctionButton
        tooltip={tooltip ?? label}
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        tooltipDisabled={tooltip === null}
        buttonStyle={evaluateFunctionalParam(
          params.buttonDesign?.buttonStyle ?? ButtonStyle.TERTIARY_GRAY,
          actionFunctionParams
        )}
        disabled={Boolean(params.getDisabled?.(actionFunctionParams))}
        icon={evaluateFunctionalParam(params.icon, actionFunctionParams)}
        iconAltText={params.iconAltText ?? tooltipText ?? label}
        buttonText={buttonText}
        onClick={() => {
          params.onInvoke(actionFunctionParams);
        }}
      />
    );
  };

  const renderOverflowMenuItem: (
    actionFunctionParams: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => OverflowMenuOption = (
    actionFunctionParams: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ) => {
    const label = evaluateFunctionalParam(params.label, actionFunctionParams);
    let tooltip = params.getTooltip?.(actionFunctionParams);
    tooltip = typeof tooltip === 'string' ? tooltip : undefined; // Does not support JSX tooltips
    let icon: BenchlingIconName | undefined | JSX.Element = evaluateFunctionalParam(
      params.icon,
      actionFunctionParams
    );
    icon = typeof icon === 'string' ? icon : undefined; // Does not support JSX icons
    return {
      icon,
      contents: <span>{label}</span>,
      onSelect: () => {
        params.onInvoke(actionFunctionParams);
      },
      isDisabled: Boolean(params.getDisabled?.(actionFunctionParams)),
      tooltipTitle: tooltip,
      // TODO: remove duplicative tooltipText param after confirming that no usages of this attribute exist
      tooltipText: tooltip,
      dataTestAttrs: {
        component: 'MoreActionFunctionsButton',
        element: 'overflowMenuItem',
        key: label && phraseToCamelCase(label),
      },
    };
  };

  return makeCustomButtonActionFunction({
    getHidden: params.getHidden,
    renderToolbarButton,
    renderOverflowMenuItem,
  });
}

/**
 * Returns param if it is not a function, otherwise evaluates param using actionFunctionParams.
 */
function evaluateFunctionalParam<T, TTableMetadata, TRowMetadata>(
  param: Exclude<T, Function> | ActionParamCallback<TTableMetadata, TRowMetadata, T>,
  actionFunctionParams: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
): T {
  return typeof param === 'function'
    ? (param as ActionParamCallback<TTableMetadata, TRowMetadata, T>)(actionFunctionParams)
    : param;
}

const tableStatusesThatAreSubmitted = [
  TableIndicatorStatus.NO_STATUS,
  TableIndicatorStatus.SUBMITTED_BYPASSING_VALIDATION,
  TableIndicatorStatus.OUT_OF_DATE,
  TableIndicatorStatus.ROWS_ARCHIVED,
];

/**
 * Returns a callback to get the icon for an action function.
 * The callback returns the column-mapping icon if the table is a source for any table mapping.
 * Otherwise, the callback returns fallbackIcon.
 */
export function getSubmitActionFunctionIcon<TTableMetadata, TRowMetadata>(
  fallbackIcon?: BenchlingIconName
): ActionParamCallback<TTableMetadata, TRowMetadata, BenchlingIconName | JSX.Element> {
  return (
    params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>
  ): BenchlingIconName | JSX.Element => {
    let icon = fallbackIcon ?? params.structuredTableAdapter.getTableIcon(params.structuredTable);
    const mappingsState = StructuredTableMappingsGlobalState.getState(params.entryOrTemplateId ?? '');
    // At least one mapping exists with this table as a source
    if (
      StructuredTableMappingsStateManager.hasMappingsFromSourceTable(
        mappingsState,
        params.structuredTable.provenanceUuid
      )
    ) {
      icon = 'column-mapping';
    }
    return getSubmitIconForTableStatus(params.tableStatus, icon);
  };
}

export function getSubmitIconForTableStatus(
  tableStatus: TableIndicatorStatus | undefined,
  fallbackIcon: BenchlingIconName
): BenchlingIconName | JSX.Element {
  if (
    tableStatus === TableIndicatorStatus.NO_STATUS ||
    tableStatus === TableIndicatorStatus.ROWS_ARCHIVED ||
    tableStatus === TableIndicatorStatus.OUT_OF_DATE
  ) {
    return <BenchlingIcon icon="done" className="mediocre-table-submit-button-icon--submitted" />;
  }
  if (tableStatus === TableIndicatorStatus.SUBMITTED_BYPASSING_VALIDATION) {
    return <BenchlingIcon icon="warning" className="mediocre-table-submit-button-icon--bypassed-errors" />;
  }
  return fallbackIcon;
}

export function getSubmitButtonLabel(tableStatus: TableIndicatorStatus | undefined): string {
  return tableStatus && tableStatusesThatAreSubmitted.includes(tableStatus) ? 'Submitted' : 'Submit';
}

/**
 * If the table has bypassable errors or was submitted bypassing validation, return a red button.
 * Else, return the provided color, or default to ButtonStyle.TERTIARY_GRAY if none was given.
 */
export function getSubmitButtonStyle(
  tableStatus: TableIndicatorStatus | undefined,
  defaultButtonStyle: ButtonStyle = ButtonStyle.TERTIARY_GRAY
): ButtonStyle {
  if (tableStatus === TableIndicatorStatus.HAS_BYPASSABLE_ERRORS) {
    return ButtonStyle.NEGATIVE;
  }
  if (tableStatus && tableStatusesThatAreSubmitted.includes(tableStatus)) {
    return ButtonStyle.TRANSPARENT;
  }

  return defaultButtonStyle;
}

/**
 * When collapsed, we should show "Submitted" but not the submit button.
 * When not collapsed, this should always be shown.
 */
export function getSubmitButtonHidden(
  tableStatus: TableIndicatorStatus | undefined,
  isCollapsed: boolean = false
): boolean {
  return isCollapsed && (!tableStatus || !tableStatusesThatAreSubmitted.includes(tableStatus));
}
